import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Bell,
  Menu,
  Plus,
  User2,
} from "lucide-react";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/date";
import { useAuth } from "@/context/AuthContext";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import CreateTicketDialog from "@/components/tickets/CreateTicketDialog";
import ThemeToggle from "@/components/common/ThemeToggle";
import Logo from "@/components/common/Logo";
import { ROLES, hasPermission } from "@/lib/constants";

export default function Topbar({ onMenuClick }) {
  const { user } = useAuth();
  const [notifs, setNotifs] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const navigate = useNavigate();

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/notifications", {
        params: { unread_only: false },
      });
      setNotifs(data || []);
    } catch (_error) {
      // Ignore notification polling failures.
    }
  };

  useEffect(() => {
    loadNotifs();
    const intervalId = setInterval(loadNotifs, 20000);

    return () => clearInterval(intervalId);
  }, []);

  const unread = notifs.filter((notification) => !notification.read).length;

  const canCreate =
    user &&
    [
      ROLES.BUSINESS_USER,
      ROLES.CLIENT_ADMIN,
      ROLES.CLIENT_USER,
      ROLES.BUSINESS_ADMIN,
      ROLES.SUPER_ADMIN,
      ROLES.SUPPORT_ADMIN,
    ].includes(user.role) &&
    hasPermission(user, "create_tickets");

  return (
    <>
      <header className="sticky top-0 z-30 flex min-h-[58px] items-center gap-2 border-b border-border bg-background/90 px-3 py-2 backdrop-blur-md sm:px-4 lg:min-h-[65px] lg:gap-4 lg:px-6 lg:py-3">
        <button
          type="button"
          onClick={onMenuClick}
          aria-label="Open sidebar"
          data-testid="mobile-sidebar-open"
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:border-[#d4af37]/40 hover:text-[#e5c158] lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>

        <div className="min-w-0 flex-1 lg:hidden">
          <Logo size={25} showText />
        </div>

        <div className="ml-auto flex flex-shrink-0 items-center gap-1.5 sm:gap-2">
          {canCreate && (
            <button
              type="button"
              data-testid="topbar-create-ticket"
              onClick={() => setOpenCreate(true)}
              className="inline-flex h-10 items-center gap-2 rounded-md bg-gradient-to-b from-[#e5c158] to-[#d4af37] px-3 text-sm font-semibold text-black shadow-lg shadow-[#d4af37]/20 transition-colors hover:from-[#f0cf6d] hover:to-[#e5c158]"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">New Ticket</span>
            </button>
          )}

          <div className="hidden sm:block">
            <ThemeToggle />
          </div>

          <button
            type="button"
            data-testid="topbar-profile"
            onClick={() => navigate("/profile")}
            className="hidden h-10 w-10 items-center justify-center rounded-md border border-border transition-colors hover:border-[#d4af37]/40 sm:flex"
            aria-label="Profile"
          >
            <User2 className="h-4 w-4" />
          </button>

          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="topbar-notifications-trigger"
                className="relative flex h-10 w-10 items-center justify-center rounded-md border border-border hover:border-[#d4af37]/40"
                aria-label="Notifications"
              >
                <Bell className="h-4 w-4" />

                {unread > 0 && (
                  <span
                    data-testid="notif-badge"
                    className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] text-white"
                  >
                    {unread}
                  </span>
                )}
              </button>
            </PopoverTrigger>

            <PopoverContent
              align="end"
              sideOffset={8}
              className="w-[calc(100vw-1rem)] max-w-96 border-border p-0 glass"
            >
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div className="text-sm font-semibold">Notifications</div>

                <button
                  type="button"
                  onClick={async () => {
                    await api.post("/notifications/read-all");
                    loadNotifs();
                  }}
                  className="text-xs text-muted-foreground hover:text-[#e5c158]"
                  data-testid="mark-all-read"
                >
                  Mark all read
                </button>
              </div>

              <div className="max-h-[60vh] divide-y divide-border overflow-y-auto scrollbar-thin">
                {notifs.length === 0 && (
                  <div className="p-6 text-center text-xs text-muted-foreground">
                    No notifications yet
                  </div>
                )}

                {notifs.slice(0, 20).map((notification) => {
                  const initial = String(notification.title || "N")
                    .trim()
                    .charAt(0)
                    .toUpperCase();

                  return (
                    <button
                      type="button"
                      key={notification.id}
                      data-testid={`notif-item-${notification.id}`}
                      onClick={async () => {
                        await api.post(
                          `/notifications/${notification.id}/read`
                        );
                        loadNotifs();

                        if (notification.ticket_id) {
                          navigate(`/tickets/${notification.ticket_id}`);
                        }
                      }}
                      className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary/30 ${
                        !notification.read ? "bg-primary/[0.06]" : ""
                      }`}
                    >
                      <div
                        className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold ${
                          !notification.read
                            ? "border-primary/35 bg-primary/10 text-primary"
                            : "border-border bg-secondary/60 text-muted-foreground"
                        }`}
                      >
                        {initial}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-start gap-2">
                          <div
                            className={`truncate text-xs ${
                              !notification.read
                                ? "font-semibold text-foreground"
                                : "font-medium"
                            }`}
                          >
                            {notification.title}
                          </div>

                          {!notification.read && (
                            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                          )}
                        </div>

                        {notification.body && (
                          <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                            {notification.body}
                          </div>
                        )}

                        <div className="mt-1 text-[10px] text-muted-foreground/70">
                          {formatDateTime(notification.created_at)}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </header>

      <CreateTicketDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        onCreated={(ticket) => {
          window.dispatchEvent(new Event("helpdesk-ticket-changed"));
          navigate(`/tickets/${ticket.id}`);
        }}
      />
    </>
  );
}
